import { useRef, useState, useCallback } from 'react'
import * as R from 'remeda'
import { nanoid } from 'nanoid'
import invariant from 'tiny-invariant'
import { DistributedOmit } from 'type-fest'
import { captureException } from '@chilipiper/sentry'
import { PublishCallback } from '../chat-data/observableResource'
import { Affiliations, Marker, Room, RoomId } from '../chat-data/types'
import { useChatDataState } from '../chat-data/useChatDataState'
import { exponentialSleep } from '../helpers/exponentialSleep'
import { getLastEventWithPreview } from '../helpers/getLastEventWithPreview'
import { createClient, Credentials, EventStatus, Status } from './XMPPClient'
import { PayloadIn, TypedEnvelopeOut } from './types'
import { getNewRoomAssignees, RoomAssignees } from './getNewRoomAssignees'
import { DEFAULT_MAX_ROOMS_INBOX_CHUNK } from './constants'

export type ClientEvents = {
  event: {
    data: {
      client: 'private' | 'public'
      isUnread: boolean
      payload: PayloadIn
      /**
       * If true, the event is from a MAM request. This means it's not a new event, but an event
       * that was sent before and retreived when looking up history
       */
      isMam: boolean
    }
  }
  roomRead: { data: RoomId }
}

export const useXMPPClient = (
  clientId: 'private' | 'public',
  publish: PublishCallback<ClientEvents>
) => {
  const [client] = useState(() => createClient(clientId))
  const [userId, setUserId] = useState<string>()
  const [status, setStatus] = useState<Status>('DISCONNECTED')
  const {
    value,
    setters: { setEvent, setEvents },
  } = useChatDataState()
  const { isMarked, markEvent: registerMarkEvent } = useMarkersStore()
  const [roomAssignees, setRoomAssignees] = useState<RoomAssignees>({})

  const publishEvent = useCallback(
    (event: ClientEvents['event']['data']) => {
      setEvent(event.payload, eventItem => {
        if (eventItem.type === 'Marker') {
          return
        }
        publish('event', {
          client: clientId,
          payload: eventItem,
          isUnread: event.isUnread,
          isMam: event.isMam,
        })
      })
    },
    [clientId, publish, setEvent]
  )

  const envelopeOutToPayloadIn = useCallback(
    (roomId: string, envelopeOut: TypedEnvelopeOut): PayloadIn => {
      invariant(userId, 'userId must be defined to convert payloadOut to payloadIn')
      const basePayload = {
        ...envelopeOut.payload,
        author: userId,
        sent: false,
        id: nanoid(),
        roomId: roomId,
        statuses: {},
        timestamp: new Date().toISOString(),
      }

      return basePayload
    },
    [userId]
  )

  const start = useCallback(
    (
      tenantId: string,
      workspaceId: string | undefined,
      getConfig: (isRetrying: boolean) => Promise<{
        credentials: Credentials
      }>
    ) => {
      setStatus('CONNECTING')
      return withExponentialRetry(
        async retry => {
          const { credentials } = await getConfig(retry === 0)
          client.auth(credentials, tenantId)
          client.onStatusChange(value => {
            setStatus(value)
          })

          client.setupListeners({
            onConversationStatusChange: event => {
              publishEvent({
                client: clientId,
                payload: event,
                isUnread: false,
                isMam: false,
              })
            },
            onGroupchatMessageFailed: e => {
              publishEvent({
                client: clientId,
                payload: e,
                isUnread: !isMarked(e.roomId, e),
                isMam: false,
              })
            },
            onGroupchatMarker: e => {
              setEvent(e)
              registerMarkEvent(e.roomId, e.eventId)
            },
            onGroupchatMessage: e => {
              publishEvent({
                client: clientId,
                payload: e,
                isUnread: !isMarked(e.roomId, e),
                isMam: false,
              })
            },
            onAffiliationChange: e => {
              setRoomAssignees(prevRoomAssignees =>
                getNewRoomAssignees({
                  prevRoomAssignees,
                  affiliationEvent: e,
                })
              )
            },
            onArchivedAffiliationsChange: e => {
              setRoomAssignees(prevRoomAssignees =>
                getNewRoomAssignees({
                  prevRoomAssignees,
                  affiliationEvent: e,
                })
              )
            },
          })
          await client.start().catch(error => {
            console.error('Error: ', error)
            throw error
          })
          setUserId(credentials.username.split('@')[0])
        },
        CONNECTION_RETRIES,
        250
      ).then(
        () => {
          client.onError((error, stopClient) => {
            console.error('Error on XMPPClient, trying to reconnect', error)
            stopClient()
              .then(R.doNothing, R.doNothing)
              .then(() => start(tenantId, workspaceId, getConfig))
              .then(R.doNothing, R.doNothing)
          })
          setStatus('CONNECTED')
        },
        () => {
          setStatus('ERROR')
        }
      )
    },
    [client, publishEvent, clientId, isMarked, setEvent, registerMarkEvent]
  )

  const stop = useCallback(async () => {
    await client.stop()
    setStatus('DISCONNECTED')
  }, [client])

  const sendEvent = useCallback(
    async (
      toRoomId: string,
      event: DistributedOmit<TypedEnvelopeOut, 'envelopeId'>,
      optimisticUpdate?: boolean
    ) => {
      let eventToSend: TypedEnvelopeOut
      if (event.type === 'BotEnvelope') {
        const lastEnvelopeId =
          R.pipe(
            value.events[toRoomId] ?? {},
            R.values(),
            R.filter(e => e?.author !== userId),
            R.sortBy(e => e?.timestamp ?? ''),
            R.findLast(e => Boolean(e && 'envelopeId' in e)),
            e => (e && 'envelopeId' in e ? e.envelopeId : 0)
          ) ?? 0
        eventToSend = {
          ...event,
          envelopeId: lastEnvelopeId + 1,
        }
      } else {
        eventToSend = event
      }
      let id = nanoid()
      if (optimisticUpdate) {
        const optimisticPayload = envelopeOutToPayloadIn(toRoomId, eventToSend)
        publishEvent({
          client: clientId,
          payload: optimisticPayload,
          isUnread: false,
          isMam: false,
        })
        if (optimisticPayload.id) {
          id = optimisticPayload.id
        }
      }
      await client.sendEvent(toRoomId, eventToSend, id).catch(error => {
        console.error(`Error: ${error} - Failed to send event: ${JSON.stringify(eventToSend)}`)
      })
      return id
    },
    [client, clientId, envelopeOutToPayloadIn, publishEvent, userId, value.events]
  )

  const markEvent = useCallback(
    async (to: string, eventId: string, status: EventStatus) => {
      await client.markEvent(to, eventId, status).catch(error => {
        console.error(`Error: ${error} - Failed to mark event: ${eventId}`)
      })
      registerMarkEvent(to, eventId)
      const event = value.events[to]?.[eventId]
      if (event && event.type !== 'Marker') {
        publishEvent({ client: clientId, payload: event, isUnread: false, isMam: false })
      }
    },
    [client, registerMarkEvent, value.events, publishEvent, clientId]
  )

  const loadRoomEvents = useCallback(
    async (
      roomId: string,
      params: {
        after?: string
        before?: string
        max?: number
        waitForRoomPreparation?: boolean
      } = {}
    ) => {
      const events = await client.loadRoomEvents(roomId, params)
      const markers = R.indexBy(
        events.filter((e): e is Marker => e.type === 'Marker'),
        event => event.eventId
      )
      Object.values(markers).forEach(marker => registerMarkEvent(roomId, marker.eventId))

      events
        .filter(
          (e): e is Exclude<typeof e, Marker | Affiliations> =>
            e.type !== 'Marker' && e.type !== 'Affiliations'
        )
        .map(event => {
          if (event.id) {
            const marker = markers[event.id]
            delete markers[event.id]
            return { client: clientId, isUnread: !marker, payload: event, isMam: true }
          } else {
            return { client: clientId, isUnread: true, payload: event, isMam: true }
          }
        })
        .forEach(data => publishEvent(data))
      Object.values(markers)
        .map(marker => value.events[roomId]?.[marker.eventId])
        .forEach(payload => {
          if (payload && payload.type !== 'Marker') {
            publishEvent({ client: clientId, payload, isUnread: false, isMam: true })
          }
        })
      setEvents(roomId, events)
      return events
    },
    [client, clientId, publishEvent, registerMarkEvent, setEvents, value.events]
  )

  const loadLastEventsForRoom = useCallback(
    async (room: Pick<Room, 'id' | 'lastEvents'>) => {
      if (room.lastEvents && getLastEventWithPreview(room.lastEvents)) {
        return room.lastEvents
      }
      const eventsToLoad = 5
      const events = await client.loadRoomEvents(room.id, { max: eventsToLoad })
      if (events.length < eventsToLoad || getLastEventWithPreview(events)) {
        return events
      }
      const moreEvents = await client.loadRoomEvents(room.id, { max: 50 })
      if (!getLastEventWithPreview(moreEvents)) {
        console.error(
          `Unimplemented event preview roomId: "${room.id}", events: ${JSON.stringify(moreEvents)}`
        )
      }
      return moreEvents
    },
    [client]
  )

  const loadLastEventsForRooms = useCallback(
    async (rooms: Pick<Room, 'id' | 'lastEvents'>[]) => {
      const events = await Promise.allSettled(rooms.map(room => loadLastEventsForRoom(room)))
      return R.pipe(
        events,
        R.zip(rooms),
        R.map(
          ([events, room]) =>
            [
              room.id,
              events.status === 'fulfilled'
                ? { type: 'success' as const, value: events.value }
                : { type: 'error' as const, error: events.reason },
            ] as const
        ),
        R.fromEntries()
      )
    },
    [loadLastEventsForRoom]
  )

  const loadInbox = useCallback(
    async (box: 'inbox' | 'archive' | 'all' = 'inbox', after?: string) => {
      try {
        const rooms = await client.loadInbox({ box, max: DEFAULT_MAX_ROOMS_INBOX_CHUNK, after })
        if (rooms === undefined) {
          return
        }
        const eventsByRoomId = await loadLastEventsForRooms(rooms)
        return rooms.map(room => {
          const roomEventsResult = eventsByRoomId[room.id]
          if (!roomEventsResult || roomEventsResult.type === 'error') {
            if (
              roomEventsResult?.error instanceof Error &&
              (roomEventsResult.error.message.startsWith('not-allowed') ||
                roomEventsResult.error.message.startsWith('item-not-found'))
            ) {
              return { ...room, isDisabled: true }
            } else {
              captureException(
                new Error(`Error loading events for room "${room.id}": ${roomEventsResult?.error}`)
              )
            }
            return room
          }
          const events = roomEventsResult.value ?? []
          if (events.length === 0) {
            captureException(
              new Error(`A room "${room.id}" was found, but there were no events in it`)
            )
          }
          setEvents(room.id, events)
          for (const event of events) {
            if (event.type !== 'Marker' && event.type !== 'Affiliations') {
              publishEvent({
                client: clientId,
                payload: event,
                isUnread: !isMarked(room.id, event),
                isMam: true,
              })
            } else if (event.type === 'Marker') {
              registerMarkEvent(room.id, event.eventId)
            }
          }
          return {
            ...room,
            lastEvents: events,
          }
        })
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    [client, loadLastEventsForRooms, setEvents, publishEvent, clientId, isMarked, registerMarkEvent]
  )

  const loadRoomDetails = useCallback(
    async (roomId: string) => {
      try {
        return await client.loadInboxItem(roomId)
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    [client]
  )

  const loadAffiliations = useCallback(
    async ({ roomId }: { roomId: string }) => {
      try {
        const affiliations = await client.getAffiliations(roomId)
        setRoomAssignees(prevRoomAssignees => {
          return {
            ...prevRoomAssignees,
            [roomId]: {
              version: undefined,
              timestamp: undefined,
              assignees: R.mapToObj(affiliations.users, user => [user.id, user.affiliation]),
            },
          }
        })
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    [client]
  )

  const setMuteStatus = useCallback(
    (roomId: string, isMuted: boolean) => client.setMuteStatus(roomId, isMuted),
    [client]
  )

  return {
    userId,
    start,
    stop,
    status,
    sendEvent,
    markEvent,
    loadRoomEvents,
    loadInbox,
    loadRoomDetails,
    value,
    setEvent,
    loadAffiliations,
    roomAssignees,
    setMuteStatus,
  }
}

const withExponentialRetry = async <T>(
  fn: (tryNumber: number) => Promise<T>,
  maxRetries: number,
  backoffBase: number
): Promise<T> => {
  const executeTry = async (retry: number): Promise<T> => {
    try {
      return await fn(retry)
    } catch (error) {
      if (retry >= maxRetries) {
        throw error
      }
      await exponentialSleep(retry, backoffBase)
      return executeTry(retry + 1)
    }
  }
  return executeTry(0)
}

const CONNECTION_RETRIES = 7

const useMarkersStore = () => {
  const markedEvents = useRef<{
    [roomId: string]: {
      lastMarkedEvent?: {
        id: string
        timestamp: string
      }
      markedEventsIds: Set<string>
    }
  }>({})
  return {
    markEvent: useCallback((roomId: string, eventId: string) => {
      const roomData = markedEvents.current[roomId] ?? { markedEventsIds: new Set<string>() }
      roomData.markedEventsIds.add(eventId)
      markedEvents.current[roomId] = roomData
    }, []),
    isMarked: useCallback(
      (roomId: string, event: { readonly id?: string; readonly timestamp?: string }) => {
        if (!event.id) {
          return false
        }
        const roomData = markedEvents.current[roomId]
        if (!roomData) {
          return false
        }
        if (roomData.lastMarkedEvent?.id === event.id) {
          return true
        }
        if (roomData.markedEventsIds.has(event.id)) {
          if (!event.timestamp) {
            return true
          }
          if (!roomData.lastMarkedEvent || roomData.lastMarkedEvent.timestamp < event.timestamp) {
            roomData.lastMarkedEvent = {
              id: event.id,
              timestamp: event.timestamp,
            }
          }
          if (roomData.lastMarkedEvent && event.timestamp <= roomData.lastMarkedEvent.timestamp) {
            // we can remove older markers, only the newest one is interesting,
            // anything before that is read by definition
            roomData.markedEventsIds.delete(event.id)
          }
          return true
        }
        return false
      },
      []
    ),
  }
}
